import * as PropTypes from "prop-types";
import classNames from "classnames";
import MapGL, { Marker, NavigationControl, Popup } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import markersMapStyles from "./MarkersMap.css";

import { withViewport } from "/react/utils/withViewport";
import { MarkerCluster } from "./MarkerCluster";

const ACCESS_TOKEN =
  "pk.eyJ1Ijoid2luZ2FyZCIsImEiOiJja25ydXN5dXUwcGNnMzFvOXl4Znh4a2NrIn0.5tqqoZvXGKY-j9rp9Intrw";

@withViewport()
export class MarkersMap extends React.Component {
  static propTypes = {
    markers: PropTypes.array.isRequired,
    centerCoordinates: PropTypes.object.isRequired,
    isFullscreen: PropTypes.bool.isRequired,
    title: PropTypes.string,
    zoom: PropTypes.number,
    isFlightInstructorMap: PropTypes.bool,
  };

  state = {
    isMounted: false,
    isFullscreen: false,
    showTooltip: false,
    activeMarkerIndex: null,
    mapOptions: {
      scrollZoom: false,
      dragPan: false,
      dragRotate: false,
      doubleClickZoom: true,
      touchZoom: false,
      touchRotate: true,
    },
    viewport: {
      zoom: this.props.zoom || 2,
      latitude: null,
      longitude: null,
      width: null,
      height: null,
    },
  };

  containerRef = React.createRef();

  get shouldEnableMapInteractivity() {
    return (
      this.props.viewportIsLaptopOrBigger ||
      (this.state.isFullscreen && this.props.viewportIsTabletOrSmaller)
    );
  }

  async componentDidMount() {
    const {
      centerCoordinates: { latitude, longitude },
    } = this.props;

    this.setState({ isMounted: true });
    this.centerAtCoords(latitude, longitude);

    window.addEventListener("resize", this.handleResize);

    if (this.shouldEnableMapInteractivity) {
      this.enableMapInteractivity();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isFullscreen && !this.state.isFullscreen) {
      this.setState({ isFullscreen: true });
      this.enableMapInteractivity();
    }
  }

  enableMapInteractivity = () => {
    this.setState({
      mapOptions: {
        ...this.state.mapOptions,
        touchZoom: true,
        dragPan: true,
      },
    });
  };

  handleResize = (event) => {
    this.setMapDimensions();
  };

  setMapDimensions() {
    if (!this.containerRef.current) return;

    const containerHeight =
      this.containerRef.current.getBoundingClientRect().height;
    const containerWidth =
      this.containerRef.current.getBoundingClientRect().width;
    const minHeight = 500;

    const calculatedHeight =
      containerHeight < minHeight ? minHeight : containerHeight;

    this.setState({
      viewport: {
        ...this.state.viewport,
        width: containerWidth,
        height: calculatedHeight,
      },
    });
  }

  handleViewportChange = ({ latitude, longitude, bearing, pitch, zoom }) => {
    this.setState({
      viewport: {
        ...this.state.viewport,
        latitude,
        longitude,
        bearing,
        pitch,
        zoom,
      },
    });
    // For Debugging lat/long/zoom
    // console.log("LAT:", latitude);
    // console.log("LNG:", longitude);
    // console.log("Zoom:", zoom);
  };

  handleOnMarkerClick = (marker, index) => {
    if (this.state.activeMarkerIndex === index) {
      this.closeTooltip();
    } else {
      this.openTooltip(index);
    }
  };

  handleOnMapClick = () => this.closeTooltip();

  closeTooltip() {
    this.setState({
      showTooltip: false,
      activeMarkerIndex: null,
    });
  }

  openTooltip(index) {
    this.setState({
      showTooltip: true,
      activeMarkerIndex: index,
    });
  }

  handleOnTooltipCloseClick = () => this.closeTooltip();

  handleZoomToCluster = (latitude, longitude, targetZoom, callback) => {
    this.setState({
      viewport: {
        ...this.state.viewport,
        latitude,
        longitude,
        zoom: targetZoom,
      },
    }, () => {
      setTimeout(() => {
        if (callback) callback();
      }, 300);
    });
  };

  handleOnLoad = (event) => this.setMapDimensions();

  centerAtCoords(latitude, longitude) {
    this.setState({
      viewport: {
        ...this.state.viewport,
        latitude: latitude,
        longitude: longitude,
      },
    });
  }

  getPointerEvents() {
    if (this.shouldEnableMapInteractivity) {
      return {
        pointerEvents: "all",
      };
    } else {
      return {
        pointerEvents: "none",
      };
    }
  }

  getDistanceBetweenCoords(lat1, lon1, lat2, lon2) {
    const radius = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const calc = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return radius * calc;
  }

  getClusteringThreshold(zoom) {
    if (zoom <= 3) return 500;
    if (zoom <= 5) return 200;
    if (zoom <= 7) return 50;
    if (zoom <= 9) return 10;
    if (zoom <= 11) return 2;
    if (zoom <= 13) return 0.5;
    if (zoom <= 15) return 0.1;
    return 0.01;
  }

  groupMarkersByLocation(markers, zoom) {
    const threshold = this.getClusteringThreshold(zoom);
    const clusters = [];
    const processed = new Set();

    markers.forEach((marker, index) => {
      if (processed.has(index)) return;

      const cluster = {
        latitude: marker.latitude,
        longitude: marker.longitude,
        markers: [{ marker, index }],
      };

      markers.forEach((otherMarker, otherIndex) => {
        if (otherIndex === index || processed.has(otherIndex)) return;

        const distance = this.getDistanceBetweenCoords(
          marker.latitude,
          marker.longitude,
          otherMarker.latitude,
          otherMarker.longitude
        );

        if (distance <= threshold) {
          cluster.markers.push({ marker: otherMarker, index: otherIndex });
          processed.add(otherIndex);
        }
      });

      processed.add(index);
      clusters.push(cluster);
    });

    return clusters;
  }

  render() {
    const { viewport, mapOptions, isMounted, showTooltip, activeMarkerIndex } =
      this.state;
    const { markers, isFullscreen, isFlightInstructorMap } = this.props;

    if (!isMounted) return null;

    const markerGroups = this.groupMarkersByLocation(markers, viewport.zoom);

    return (
      <div
        className={classNames(markersMapStyles.container, {
          [markersMapStyles.isFullscreen]: isFullscreen,
        })}
        style={this.getPointerEvents()}
        ref={this.containerRef}
      >
        <MapGL
          {...viewport}
          {...mapOptions}
          ref={(node) => (this.mapRef = node)}
          onViewportChange={this.handleViewportChange}
          onClick={this.handleOnMapClick}
          onLoad={this.handleOnLoad}
          mapboxApiAccessToken={ACCESS_TOKEN}
          mapStyle="mapbox://styles/wingard/cl3ulzri7008r14rwxqutdm1n"
        >
          {this.shouldEnableMapInteractivity && (
            <div className={markersMapStyles.navigationControls}>
              <NavigationControl
                showCompass={false}
                onViewportChange={this.handleViewportChange}
                // console output zoom and lat and long
              />
            </div>
          )}
          
          {isFlightInstructorMap ? ( 
            <React.Fragment>
              {markerGroups.map((group, groupIndex) => (
                <MarkerCluster
                  key={`cluster-${groupIndex}`}
                  markers={group.markers}
                  latitude={group.latitude}
                  longitude={group.longitude}
                  onMarkerClick={this.handleOnMarkerClick}
                  activeMarkerIndex={activeMarkerIndex}
                  zoom={viewport.zoom}
                  onZoomToCluster={this.handleZoomToCluster}
                  renderOnTop={true}
                />
              ))}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {markers.map((marker, index) => (
                <MapMarker
                  key={index}
                  id={index}
                  longitude={marker.longitude}
                  latitude={marker.latitude}
                  tooltip={marker.tooltip}
                  activeMarkerIndex={activeMarkerIndex}
                  onClick={() => this.handleOnMarkerClick(marker, index)}
                />
              ))}
            </React.Fragment>
          )}
          
          {markers.map((marker, index) => (
            <Tooltip
              key={index}
              {...markers[index]}
              onClose={this.handleOnTooltipCloseClick}
              className={classNames(markersMapStyles.tooltip, {
                [markersMapStyles.tooltipFadeIn]:
                  showTooltip && activeMarkerIndex === index,
              })}
              isFlightInstructorMap={isFlightInstructorMap}
            />
          ))}
        </MapGL>
      </div>
    );
  }
}

const MapMarker = (props) => {
  const size = 37;
  const offset = -1 * (size / 2);

  return (
    <Marker
      offsetLeft={offset}
      offsetTop={offset}
      key={`marker-${props.id}`}
      className={classNames(markersMapStyles.marker, {
        [markersMapStyles.markerFadeOut]: props.activeMarkerIndex === props.id,
      })}
      longitude={props.longitude}
      latitude={props.latitude}
    >
      {props.tooltip.title.includes("Victory Lane") ? (
        <MarkerIconStar size={size} onClick={props.onClick} />
      ) : (
        <MarkerIcon size={size} onClick={props.onClick} />
      )}
    </Marker>
  );
};

const MarkerIcon = (props) => {
  const { onClick, size } = props;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={size}
      viewBox="0 0 37 37"
      onClick={onClick}
    >
      <g fill="none" fillRule="evenodd" transform="translate(-7 -7)">
        <circle
          cx="25.5"
          cy="25.5"
          r="17.5"
          stroke="#FFF"
          strokeWidth="2"
          transform="rotate(45 25.5 25.5)"
        />
        <circle cx="25.5" cy="25.5" r="1.5" fill="#FFF" />
      </g>
    </svg>
  );
};

const MarkerIconStar = (props) => {
  const { onClick, size } = props;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={size}
      viewBox="0 0 163.38 155.39"
      onClick={onClick}
    >
      <g>
        <polygon
          points="81.69 0 100.98 59.35 163.38 59.35 112.9 96.03 132.18 155.39 81.69 118.71 31.2 155.39 50.49 96.03 0 59.35 62.41 59.35 81.69 0"
          fill="#c69355"
        />
      </g>
    </svg>
  );
};

const Tooltip = (props) => {
  return (
    <Popup
      closeOnClick
      longitude={props.longitude}
      anchor={props.isFlightInstructorMap && props.tooltip.services_provided ? "left" : "bottom-left"}
      latitude={props.latitude}
      onClose={props.onClose}
      closeButton={false}
      dynamicPosition={props.isFlightInstructorMap && props.tooltip.services_provided ? true : false}
      offsetTop={1}
      className={`${props.className} ${props.isFlightInstructorMap ? 'flightInstructorTooltip' : ''}`}
    >
      <div>
        <p>
          {props.tooltip.title}
          <br />
          {props.tooltip.title.includes("Victory Lane") && (
            <React.Fragment>
              <span className={markersMapStyles.goldStarASC}>
                Gold Star Authorized Service Center
              </span>
              <br />
            </React.Fragment>
          )}
          {props.tooltip.address && <span>{props.tooltip.address}<br /></span>}
          {props.tooltip.state && (
            <span>
              <a
                href={`tel:${props.tooltip.state}`}
                className={markersMapStyles.popupLink}
              >
                {props.tooltip.state}
              </a>
              <br />
            </span>
          )}
          {props.tooltip.phone && (
            <span>
              <a
                href={`tel:${props.tooltip.phone}`}
                className={markersMapStyles.popupLink}
              >
                {props.tooltip.phone}
              </a>
              <br />
            </span>
          )}
          {props.tooltip.email && (
            <span>
              <a
                href={`mailto:${props.tooltip.email}`}
                className={markersMapStyles.popupLink}
              >
                {props.tooltip.email}
              </a>
              <br />
            </span>
          )}
          {props.tooltip.country && (
            <span>
              <a
                href={`mailto:${props.tooltip.country}`}
                className={markersMapStyles.popupLink}
              >
                {props.tooltip.country}
              </a>
              <br />
            </span>
          )}
          {props.tooltip.testing && <span>{props.tooltip.testing}</span>}
          {props.tooltip.link && (
            <span>
              <br />
              <a
                href={props.tooltip.link.url}
                target={props.tooltip.link.target}
                className={markersMapStyles.popupLink}
              >
                {props.tooltip.link.title}
              </a>
            </span>
          )}
           {props.tooltip.website && (
            <span>
              <a
                href={props.tooltip.website}
                className={markersMapStyles.popupLink}
              >
                {props.tooltip.website}
              </a>
              <br />
            </span>
          )}
          {props.tooltip.callsign && (
            <span>
              <br />
              Callsign: {props.tooltip.callsign}
              <br />
            </span>
          )}
          {props.tooltip.training_location && (
            <span>
              <br />
              Training Location: <br />
              {props.tooltip.training_location}
              <br />
            </span>
          )}
          {props.tooltip.services_provided && (
            <span>
              <br />
              Services Provided: <br />
              {props.tooltip.services_provided.replace(/;/g, ', ')}
              <br />
            </span>
          )}
          {props.tooltip.models && (
            <span>
              <br />
              Models: <br />
              {props.tooltip.models.replace(/;/g, ', ')}
              <br />
            </span>
          )}
        </p>
      </div>
    </Popup>
  );
};
